import cx from "classnames";
import Omit from "lodash/omit";
import React, { useEffect } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { FileUploader } from "react-drag-drop-files";
import { useForm, Controller, useFieldArray, FormProvider } from "react-hook-form";
import { toast } from "react-toastify";

import TrashIcon from "./assets/trash.svg?react";
import TrashWhiteIcon from "./assets/trashWhite.svg?react";
import UploadIcon from "./assets/upload.svg?react";
import FileIcon from "./assets/uploadedFile.svg?react";
import { factoryUseModalX } from "@/lib/factoryUseModalX";
import fetchSingleton from "@/lib/fetchSingleton";
import { submitFunction, useFormValidation } from "@/lib/formValidationHelpers";
import { jsonToFormData } from "@/lib/jsonToFormData";
import { useAuth } from "@/lib/useAuth";
import { useCommunityId } from "@/lib/useCommunityId";
import { useData } from "@/lib/useData";
import { useFrequencyType } from "@/lib/useFrequencyType";
import { useScheduleType } from "@/lib/useScheduleType";
import PartFormDatePicker from "@/PartFormDatePicker";
import PartFormError from "@/PartFormError";
import PartFormSaveButton from "@/PartFormSaveButton";
import PartLoader from "@/PartLoader";
import PartModal, { type PartModalRef } from "@/PartModal";
import formStyles from "@/styles/form.module.scss";
import { Zone } from "@/types/api-types";
import { zoneEditorSchemaJoi } from "@/validations";

import styles from "./ModalZone.module.scss";

interface IFormInput extends Omit<Zone, "addresses_count" | "schedules"> {
  schedules?: (Partial<Zone["schedules"][number]> | undefined)[]; // allow undefined inside array
  file?: File;
}

interface IFormInputRef {
  reset: () => void;
}

export const useModalZoneEditor = factoryUseModalX({ queryName: "modalZoneEditor" });

const ZoneEditor = React.forwardRef<
  IFormInputRef,
  { setConfirmClose?: (arg0: boolean) => void }
>(({ setConfirmClose }, ref) => {
  const { isCreating, isEditing, editingId, finishInteraction } = useModalZoneEditor();

  const { communityId } = useCommunityId();

  const formMethods = useForm<IFormInput>({
    reValidateMode: "onSubmit",
    defaultValues: { community_id: communityId },
  });

  const {
    register,
    control,
    formState: { errors, dirtyFields },
    reset,
    setError,
    clearErrors,
    watch,
  } = formMethods;

  React.useImperativeHandle(ref, () => ({
    reset: () => reset(),
  }));

  const { handleSubmit, isSubmitting } = useFormValidation<IFormInput>({
    control,
    joiSchema: zoneEditorSchemaJoi,
    context: { isEditing, editingId },
    setError,
    clearErrors,
  });

  const {
    fields: scheduleFields,
    append: scheduleFieldsAppend,
    remove: scheduleFieldsRemove,
  } = useFieldArray({
    control,
    name: "schedules",
  });

  const [existingItem, existingItemLoading] = useData<Zone>(
    isEditing ? process.env.NEXT_PUBLIC_API_URL + `/api/v1/zones/${editingId}` : "",
  );

  const { frequencyTypes, isLoading: isFrequencyTypesLoading } = useFrequencyType();

  const { scheduleTypes, isLoading: isScheduleTypesLoading } = useScheduleType();

  useEffect(() => {
    if (isEditing && existingItem && frequencyTypes && scheduleTypes) {
      const existingItem1 = {
        code: existingItem.code,
        name: existingItem.name,
        community_id: existingItem.community_id,
        schedules: existingItem.schedules?.map(schedule => ({
          type_id: schedule?.type_id,
          frequency_id: schedule?.frequency_id,
          date_start: schedule?.date_start,
        })),
        // The upload Controller registers "file", so the defaults must carry
        // the key too: react-hook-form's isDirty deep-compares values against
        // defaults, and {file: undefined} vs a missing key reads as a change,
        // which made an untouched form ask "unsaved changes?" on close.
        file: undefined,
      };
      reset(existingItem1);
    }
    if (isCreating && frequencyTypes && scheduleTypes)
      reset({
        community_id: communityId,
        schedules: [
          { type_id: undefined, frequency_id: undefined, date_start: undefined },
        ],
        file: undefined,
      });
  }, [
    isCreating,
    isEditing,
    existingItem,
    communityId,
    // Here we watch for frequencyTypes and scheduleTypes to force react-hook-forms update existed values
    frequencyTypes,
    scheduleTypes,
  ]);

  const { idToken } = useAuth();

  const onSubmit: submitFunction<IFormInput> = async (data, handleApiResult) => {
    let url = process.env.NEXT_PUBLIC_API_URL + "/api/v1/zones";
    if (isEditing) url += "/" + editingId;

    const { schedules, file, ...restData } = data;

    const formData = jsonToFormData({
      ...restData,
      ...(file ? { file } : {}),
    });

    schedules?.forEach(it => {
      formData.append(
        "schedules",
        JSON.stringify({
          type_id: it?.type_id,
          frequency_id: it?.frequency_id,
          date_start: it?.date_start,
        }),
      );
    });

    const result = await fetchSingleton(url, {
      method: (isCreating && "POST") || (isEditing && "PUT") || "GET",
      body: formData,
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });

    handleApiResult(result);

    const msg = (() => {
      if (isCreating) return "Zone added successfully";
      if (isEditing) return "Zone updated successfully";
    })();
    toast.success(msg, { autoClose: 5000 });

    finishInteraction();
  };

  // Gate the "unsaved changes?" dialog on per-field dirtiness rather than
  // isDirty: dirtyFields only flags fields the user actually changed, so a
  // values-vs-defaults shape mismatch can never trigger a phantom prompt.
  const hasDirtyField = React.useMemo(() => {
    const hasDirtyLeaf = (node: unknown): boolean => {
      if (node === true) return true;
      if (Array.isArray(node)) return node.some(hasDirtyLeaf);
      if (node && typeof node === "object")
        return Object.values(node).some(hasDirtyLeaf);
      return false;
    };
    return hasDirtyLeaf(dirtyFields);
  }, [dirtyFields]);

  React.useEffect(() => {
    if (setConfirmClose) setConfirmClose(hasDirtyField);
  }, [hasDirtyField, setConfirmClose]);

  return (
    <FormProvider {...formMethods}>
      <PartLoader
        block
        active={existingItemLoading || isScheduleTypesLoading || isFrequencyTypesLoading}
      >
        <form
          onSubmit={handleSubmit(onSubmit)}
          onReset={() => {
            finishInteraction();
          }}
          className={formStyles.form}
          autoComplete="off"
        >
          <div className={formStyles.formMultipleColumns}>
            <div>
              <label className={formStyles.formLabel} htmlFor="code">
                Zone Code <sup>*</sup>
              </label>
              <input
                id="code"
                className={formStyles.formInput}
                {...register("code")}
                aria-invalid={errors.code ? "true" : "false"}
              />
              <PartFormError name="code" />
            </div>
          </div>
          <div className={formStyles.formMultipleColumns}>
            <div>
              <label className={formStyles.formLabel} htmlFor="name">
                Zone Name
              </label>
              <input
                id="name"
                className={formStyles.formInput}
                {...register("name")}
                aria-invalid={errors.name ? "true" : "false"}
              />
              <PartFormError name="name" />
            </div>
          </div>
          <hr className={formStyles.formHLine} />
          <div className={formStyles.formLabelBig}>Collection Schedule</div>
          {scheduleFields?.map((item, index) => {
            return (
              <React.Fragment key={"f:" + index}>
                <div className={formStyles.formRowWithAction} key={"d:" + index}>
                  <div className={formStyles.formMultipleColumns} key={"d:" + index}>
                    <div>
                      {index === 0 ? (
                        <label
                          className={formStyles.formLabel}
                          htmlFor={`schedules.${index}.type_id`}
                        >
                          Type <sup>*</sup>
                        </label>
                      ) : null}
                      <div className={formStyles.formSelect}>
                        <select
                          id={`schedules.${index}.type_id`}
                          {...register(`schedules.${index}.type_id`)}
                          data-placeholder={
                            !watch(`schedules.${index}.type_id`) ? "" : undefined
                          }
                          aria-invalid={
                            errors.schedules?.[index]?.type_id ? "true" : "false"
                          }
                        >
                          <option value="">Please Choose...</option>
                          {scheduleTypes?.map(item => (
                            <option value={item?.id} key={item?.id}>
                              {item?.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <PartFormError name={`schedules.${index}.type_id`} />
                    </div>
                    <div>
                      {index === 0 ? (
                        <label
                          className={formStyles.formLabel}
                          htmlFor={`schedules.${index}.frequency_id`}
                        >
                          Frequency <sup>*</sup>
                        </label>
                      ) : null}
                      <div className={formStyles.formSelect}>
                        <select
                          id={`schedules.${index}.frequency_id`}
                          {...register(`schedules.${index}.frequency_id`)}
                          data-placeholder={
                            !watch(`schedules.${index}.frequency_id`) ? "" : undefined
                          }
                          aria-invalid={
                            errors.schedules?.[index]?.frequency_id ? "true" : "false"
                          }
                        >
                          <option value="">Please Choose...</option>
                          {frequencyTypes?.map(item => (
                            <option value={item?.id} key={item?.id}>
                              {item?.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <PartFormError name={`schedules.${index}.frequency_id`} />
                    </div>
                    <div>
                      {index === 0 ? (
                        <label
                          className={formStyles.formLabel}
                          htmlFor={`schedules.${index}.date_start`}
                        >
                          Start Date <sup>*</sup>
                        </label>
                      ) : null}
                      <PartFormDatePicker
                        id={`schedules.${index}.date_start`}
                        control={control}
                        name={`schedules.${index}.date_start`}
                        placeholder="Select Date"
                        // Dec 2, 2024 (Tuesday)
                        dateFormat="MMM d, yyyy (EEE)"
                      />
                      <PartFormError name={`schedules.${index}.date_start`} />
                    </div>
                  </div>
                  <div>
                    {index === 0 ? <div className={formStyles.formLabel} /> : null}
                    <button
                      className={styles.formDeleteItemButton}
                      onClick={() => scheduleFieldsRemove(index)}
                      type="button"
                      data-test-element-type="button"
                      data-test-element-index={index}
                      data-test-action-type="remove"
                    >
                      <TrashWhiteIcon />
                    </button>
                  </div>
                </div>
              </React.Fragment>
            );
          })}
          <button
            className={styles.formAddItemButton}
            type="button"
            onClick={() =>
              scheduleFieldsAppend({
                type_id: undefined,
                frequency_id: undefined,
                date_start: undefined,
              })
            }
            data-test-element-type="button"
            data-test-action-type="add"
          >
            + Add More
          </button>
          <hr className={formStyles.formHLine} />
          <div className={formStyles.formLabelBig}>Upload Shape File</div>
          {/* Docs: https://www.npmjs.com/package/react-drag-drop-files */}
          <Controller
            control={control}
            name="file"
            render={({ field, fieldState }) => {
              const file = field.value;

              const handleRemove = (e: React.MouseEvent) => {
                e.stopPropagation();
                field.onChange(undefined); // Clear file
              };

              const handleChange = (file: File | File[]) => {
                field.onChange(Array.isArray(file) ? file[0] : file);
              };

              return (
                <FileUploader
                  classes={cx("uploadZone", formStyles.formUploadZone, {
                    [formStyles.invalid]: !!fieldState.error,
                  })}
                  handleChange={handleChange}
                  types={["KML"]}
                  disabled={!!file}
                >
                  {!file ? (
                    <div className={styles.uploadFieldContainerWithoutFile}>
                      <UploadIcon />
                      <span>
                        Drag & drop shape file here or <i>Choose file</i> to upload
                      </span>
                    </div>
                  ) : (
                    <div className={styles.uploadFieldContainerWithFile}>
                      <FileIcon />
                      <div className={styles.fileInfo}>
                        <div className={styles.fileInfoName}>{file.name}</div>
                        <div className={styles.fileInfoSize}>
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleRemove}
                        className={styles.removeFileButton}
                        data-test-element-type="button"
                        data-test-action-type="remove"
                      >
                        <TrashIcon />
                      </button>
                    </div>
                  )}
                </FileUploader>
              );
            }}
          />
          <PartFormError name="file" />

          <div className={formStyles.formActionButtonsContainer}>
            <button
              type="reset"
              className={formStyles.buttonReset}
              data-test-element-type="button"
              data-test-action-type="reset"
            >
              Discard
            </button>
            <PartFormSaveButton isSubmitting={isSubmitting}>Save</PartFormSaveButton>
          </div>
        </form>
      </PartLoader>
    </FormProvider>
  );
});

ZoneEditor.displayName = "ZoneEditor";

const ModalZone = React.forwardRef<PartModalRef>((props, ref) => {
  const { isCreating, isEditing, finishInteraction } = useModalZoneEditor();

  const title =
    (isCreating && "Add New Zone") || (isEditing && "Edit Zone") || "Unknown action";

  const [shouldConfirmClose, setShouldConfirmClose] = React.useState<boolean>(false);

  const formRef = React.useRef<IFormInputRef>(null);

  return (
    <PartModal
      ref={ref}
      title={title}
      showed={isCreating || isEditing}
      onClose={() => finishInteraction()}
      afterClose={() => formRef.current?.reset()}
      confirmCloseQuestion={shouldConfirmClose}
    >
      <ZoneEditor setConfirmClose={setShouldConfirmClose} ref={formRef} />
    </PartModal>
  );
});

ModalZone.displayName = "ModalZone";

export default ModalZone;
